import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext } from "react";
import { BentoBrand } from "schemas/dashboard";

import { getIconsCondition } from "../DetailedBrandView/helpers";

const styles = {
  icon: {
    fontSize: { xs: 14 },
    mx: 0.5,
  },
  paidPartnershipBadge: {
    color: "#32885F",
  },
  worksWithUgcBadge: {
    color: "#388BED",
  },
  worksWithInfluencer: {
    color: "#D45FD1",
  },
  bolt: {
    color: "#786EEB",
  },
  star: {
    color: "#EDDB38",
  },
  locationPin: {
    color: "#CE6868",
  },
  tiktok: {
    color: "#010101",
  },
  directContact: {
    color: "#F6BB75",
  },
  ads: {
    color: "#F15E5E",
  },
  tag: {
    fontSize: 12,
    color: grey[600],
    textTransform: "capitalize",
  },
};

interface Props {
  bentoBrand: BentoBrand;
  isBrandView?: boolean;
}

export default function BrandIcons({ bentoBrand, isBrandView }: Props) {
  const { profile } = useContext(OrganizationUserContext);

  const {
    addedWithinLastWeek,
    hasPaidPartnership,
    worksWithUgc,
    worksWithInfluencer,
    noRecentContacts,
    hasTiktokAds,
    hasNonGenericEmails,
    hasAds,
  } = getIconsCondition(bentoBrand);

  const isUserCity = () => {
    if (!profile?.city || !(profile?.city?.length > 0)) {
      return;
    }
    return bentoBrand?.locationNames?.includes(profile?.city?.toLowerCase());
  };

  const getContainerStyles = () => {
    const styles = {
      ml: -0.5,
      p: 0,
      mb: 0,
    };
    return styles;
  };

  const renderIcons = () => {
    return (
      <Grid
        item
        container
        xs="auto"
        alignItems="center"
        sx={getContainerStyles()}
      >
        {isUserCity() && (
          <Tooltip enterTouchDelay={0} title="Close to your city">
            <Box
              component="i"
              className="fa-solid fa-location-pin"
              sx={[styles.icon, styles.locationPin]}
            />
          </Tooltip>
        )}
        {hasPaidPartnership && (
          <Tooltip enterTouchDelay={0} title="Partners With Creators">
            <Box
              component="i"
              className="fa-solid fa-suitcase"
              sx={[styles.icon, styles.paidPartnershipBadge]}
            />
          </Tooltip>
        )}
        {worksWithUgc && (
          <Tooltip enterTouchDelay={0} title="Works with UGC creators">
            <Box
              component="i"
              className="fa-solid fa-tag"
              sx={[styles.icon, styles.worksWithUgcBadge]}
            />
          </Tooltip>
        )}
        {worksWithInfluencer && (
          <Tooltip enterTouchDelay={0} title="Works with Influencers">
            <Box
              component="i"
              className="fa-solid fa-hashtag"
              sx={[styles.icon, styles.worksWithInfluencer]}
            />
          </Tooltip>
        )}
        {addedWithinLastWeek && (
          <Tooltip enterTouchDelay={0} title="Added within last week">
            <Box
              sx={[styles.icon, styles.bolt]}
              component="i"
              className="fa-solid fa-bolt"
            />
          </Tooltip>
        )}
        {hasTiktokAds && (
          <Tooltip enterTouchDelay={0} title="Runs TikTok Ads">
            <Box
              component="i"
              sx={[styles.icon, styles.tiktok]}
              className="fa-brands fa-tiktok"
            />
          </Tooltip>
        )}
        {hasAds && (
          <Tooltip enterTouchDelay={0} title="Has Ads">
            <Box
              component="i"
              sx={[styles.icon, styles.ads]}
              className="fa-solid fa-rectangle-ad"
            />
          </Tooltip>
        )}
        {noRecentContacts && (
          <Tooltip enterTouchDelay={0} title="Not contacted this week">
            <Box
              component="i"
              sx={[styles.icon, styles.star]}
              className="fa-solid fa-star"
            />
          </Tooltip>
        )}
        {hasNonGenericEmails && (
          <Tooltip enterTouchDelay={0} title="Has a direct contact">
            <Box
              component="i"
              sx={[styles.icon, styles.directContact]}
              className="fa-solid fa-user"
            />
          </Tooltip>
        )}
      </Grid>
    );
  };

  const renderForBrandsView = () => {
    return (
      <>
        {isUserCity() && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              sx={[styles.icon, styles.locationPin]}
              component="i"
              className="fa-solid fa-location-pin fa-fw"
            />
            <Typography gutterBottom>Close to your city</Typography>
          </Grid>
        )}
        {hasPaidPartnership && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              component="i"
              className="fa-solid fa-briefcase fa-fw"
              sx={[styles.icon, styles.paidPartnershipBadge]}
            />
            <Typography gutterBottom>Verified Paid Partnership</Typography>
          </Grid>
        )}
        {worksWithUgc && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              component="i"
              className="fa-solid fa-tag fa-fw"
              sx={[styles.icon, styles.worksWithUgcBadge]}
            />
            <Typography gutterBottom>Works with UGC creators</Typography>
          </Grid>
        )}
        {worksWithInfluencer && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              component="i"
              className="fa-solid fa-hashtag fa-fw"
              sx={[styles.icon, styles.worksWithInfluencer]}
            />
            <Typography gutterBottom>Works with Influencers</Typography>
          </Grid>
        )}

        {addedWithinLastWeek && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              sx={[styles.icon, styles.bolt]}
              component="i"
              className="fa-solid fa-bolt fa-fw"
            />
            <Typography gutterBottom>Added within last week</Typography>
          </Grid>
        )}

        {hasTiktokAds && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              sx={[styles.icon, styles.tiktok]}
              component="i"
              className="fa-brands fa-tiktok fa-fw"
            />
            <Typography gutterBottom>Runs TikTok Ads</Typography>
          </Grid>
        )}

        {hasAds && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              sx={[styles.icon, styles.ads]}
              component="i"
              className="fa-solid fa-rectangle-ad fa-fw"
            />
            <Typography gutterBottom>Has Ads</Typography>
          </Grid>
        )}

        {noRecentContacts && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              component="i"
              sx={[styles.icon, styles.star]}
              className="fa-solid fa-star fa-fw"
            />
            <Typography gutterBottom>Not contacted this week</Typography>
          </Grid>
        )}

        {hasNonGenericEmails && (
          <Grid container alignItems="center" item xs={12}>
            <Box
              component="i"
              sx={[styles.icon, styles.directContact]}
              className="fa-solid fa-user fa-fw"
            />
            <Typography gutterBottom>Has a direct contact</Typography>
          </Grid>
        )}
      </>
    );
  };

  if (isBrandView) {
    return renderForBrandsView();
  } else {
    return renderIcons();
  }
}
